import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getRequest } from "utils/httpClient";
import { getAvailableGroupsPath } from "utils/backend-path-builders";

type Mode = "all" | "mine";

interface GroupRow {
  id: string;
  name: string;
  uri: string;
}

const GroupSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("all");
  const [filter, setFilter] = useState("");
  const [allGroups, setAllGroups] = useState<GroupRow[] | null>(null);
  const [myGroups, setMyGroups] = useState<GroupRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRequest("/v2/groups");
      setAllGroups(res || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load all groups");
    } finally {
      setLoading(false);
    }
  };

  const fetchMine = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRequest(getAvailableGroupsPath());
      setMyGroups(res || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load my groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "all" && allGroups === null) fetchAll();
    if (mode === "mine" && myGroups === null) fetchMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const groups = mode === "all" ? allGroups : myGroups;

  const filtered = useMemo(() => {
    if (!groups) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        g.name?.toLowerCase().includes(q) || g.uri?.toLowerCase().includes(q)
    );
  }, [groups, filter]);

  const refresh = () => (mode === "all" ? fetchAll() : fetchMine());

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Form.Check
          inline
          type="radio"
          id="group-switcher-all"
          label="All groups"
          checked={mode === "all"}
          onChange={() => setMode("all")}
        />
        <Form.Check
          inline
          type="radio"
          id="group-switcher-mine"
          label="My groups"
          checked={mode === "mine"}
          onChange={() => setMode("mine")}
        />
        <Form.Control
          size="sm"
          style={{ maxWidth: 240 }}
          placeholder="Filter by name or slug"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Button size="sm" variant="outline-secondary" onClick={refresh}>
          Refresh
        </Button>
        {groups && (
          <span className="text-muted small">
            {filtered.length} / {groups.length}
          </span>
        )}
      </div>

      {loading && <Spinner animation="border" size="sm" />}
      {error && <div className="text-danger small mb-2">{error}</div>}

      {!loading && groups && (
        <ListGroup
          style={{ maxHeight: 400, overflowY: "auto" }}
        >
          {filtered.map((g) => (
            <ListGroup.Item
              key={g.id}
              action
              onClick={() => navigate(`/g/${g.uri}`)}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <div style={{ fontWeight: 600 }}>{g.name}</div>
                <div className="text-muted small" style={{ fontFamily: "monospace" }}>
                  /g/{g.uri}
                </div>
              </div>
              <Button
                size="sm"
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/g/${g.uri}`);
                }}
              >
                Go
              </Button>
            </ListGroup.Item>
          ))}
          {filtered.length === 0 && (
            <ListGroup.Item className="text-muted">No groups</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default GroupSwitcher;
